// useGlobalShortcuts.js — App-level shortcuts that work regardless of
// which view/session is active. Complements useSessionShortcuts (which
// only fires when a session is open).
//
// All shortcuts dispatch CustomEvents on `window` so any mounted
// component (sidebar, modal host) can react without prop-drilling.
//
// Bindings:
//   ⌘N        → 'apolo:newSession'         (open NewSessionModal)
//   ⌘K        → 'apolo:openSkillPalette'   (Sprint v8 C2 — Command Palette de skills)
//   ⇧⌘T       → 'apolo:insertComposer'     (Sprint v8 C3 — /tdd workflow)
//   ⇧⌘B       → 'apolo:insertComposer'     (Sprint v8 C3 — /diagnose, B = "Bug")
//   ⇧⌘G       → 'apolo:insertComposer'     (Sprint v8 C3 — /grill-me)
//   ⇧⌘H       → 'apolo:insertComposer'     (Sprint v8 C3 — /handoff)
//   ⇧⌘I       → 'apolo:insertComposer'     (Sprint v8 C3 — /improve-codebase-architecture)
//   ⇧⌘Z       → 'apolo:insertComposer'     (Sprint v8 C3 — /zoom-out — overrides browser undo only when not editing)
//   ⇧⌘K       → 'apolo:openPlugins'        (open PluginsView — K for "kits")
//   ⇧⌘O       → 'apolo:openCommandCenter'  (open CommandCenterModal — O for "overview")
//   ⇧⌘E       → 'apolo:openAdapters'       (open Adapters tab inside session)
//   ⌘,        → 'apolo:openSettings'       (placeholder, future)
//
// macOS conventions respected: typing in inputs/textareas never hijacked.

// Sprint v8 C3 — Matt Pocock workflow shortcuts (shift+meta+key → /command).
// /prototype was dropped from the table because ⇧⌘P collides with Preview
// (Sprint v4) and Preview is used far more often than the prototype skill.
const WORKFLOW_SHORTCUTS = {
  t: '/tdd',
  b: '/diagnose',
  g: '/grill-me',
  h: '/handoff',
  i: '/improve-codebase-architecture',
  z: '/zoom-out',
};

import { useEffect } from 'react';

function targetIsTypingTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

// ⌘K é universal (Spotlight/Cmd Palette pattern Linear/Raycast/Notion) e
// deve funcionar mesmo dentro de inputs e textareas — Pedro está sempre
// digitando no composer. Mesma exceção pra ⌘N (Nova sessão).
const TYPING_TARGET_ALLOWED = new Set(['k', 'n']);

export function useGlobalShortcuts() {
  useEffect(() => {
    function handler(e) {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const shift = e.shiftKey;
      const key = e.key.toLowerCase();
      const inTyping = targetIsTypingTarget(e.target);
      // Bloqueia em typing target SALVO os atalhos universais (⌘K / ⌘N).
      if (inTyping && !(TYPING_TARGET_ALLOWED.has(key) && !shift)) return;

      // ⌘N — Nova sessão (no shift). Browser default would open a window;
      // we preventDefault so the in-app modal opens instead.
      if (!shift && key === 'n') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('apolo:newSession'));
        return;
      }
      // ⌘K — Skill Command Palette (Sprint v8 C2). No shift to keep it
      // ergonomic; ⇧⌘K still owns Plugins & Skills (the catalog browser).
      if (!shift && key === 'k') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('apolo:openSkillPalette'));
        return;
      }
      // Sprint v8 C3 — workflow shortcuts (⇧⌘T tdd, ⇧⌘B diagnose, ⇧⌘G grill-me,
      // ⇧⌘H handoff, ⇧⌘I improve-arch, ⇧⌘Z zoom-out). Each dispatches an
      // insertComposer event so the focused composer prepends the slash command.
      // ⇧⌘T also dispatches the legacy openSchedule for backwards compatibility
      // (sessions without the new MP plugins still need the schedule modal).
      if (shift && WORKFLOW_SHORTCUTS[key]) {
        e.preventDefault();
        const cmd = WORKFLOW_SHORTCUTS[key];
        window.dispatchEvent(
          new CustomEvent('apolo:insertComposer', { detail: { text: cmd + ' ' } }),
        );
        return;
      }
      // ⇧⌘K — Plugins & Skills modal (K for "kits", since ⇧⌘P is Preview tab)
      if (shift && key === 'k') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('apolo:openPlugins'));
        return;
      }
      // ⇧⌘O — Command Center modal (O for "overview" — board macro de sessões)
      if (shift && key === 'o') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('apolo:openCommandCenter'));
        return;
      }
      // ⇧⌘E — Adapters tab (only works inside a session)
      if (shift && key === 'e') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('apolo:openAdapters'));
        return;
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}
